import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SiteFooter from './components/SiteFooter';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CaseDetailPage from './pages/CaseDetailPage';
import ReportCasePage from './pages/ReportCasePage';
import SearchPage from './pages/SearchPage';
import SubmitSightingPage from './pages/SubmitSightingPage';
import SubscriptionPage from './pages/SubscriptionPage';
import MySubscriptionsPage from './pages/MySubscriptionsPage';
import ModerationDashboardPage from './pages/ModerationDashboardPage';
import PricingPage from './pages/PricingPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminPage from './pages/AdminPage';
import AdminInvoicesPage from './pages/AdminInvoicesPage';
import AdminBackupsPage from './pages/AdminBackupsPage';
import ConsentPage from './pages/ConsentPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import DisclaimerPage from './pages/DisclaimerPage';
import AntiHarassmentPolicyPage from './pages/AntiHarassmentPolicyPage';
import ModeratorPage from './pages/ModeratorPage';
import QRCodePage from './pages/QRCodePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import ShippingDeliveryPolicyPage from './pages/ShippingDeliveryPolicyPage';
import PrivacySettingsPage from './pages/PrivacySettingsPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// XSS hardening: every request carries the httpOnly session cookie by default,
// so we never need to persist JWTs in localStorage. Tokens returned by the
// backend live only in-memory (React state) for the lifetime of the tab; a
// page refresh authenticates purely via the session cookie.
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Ephemeral in-memory JWT. Never persisted. Populated after login/register/
  // google session, cleared on logout. On page refresh it starts null and the
  // httpOnly session cookie takes over.
  const [token, setToken] = useState(null);
  const justAuthenticated = useRef(false);

  const checkAuth = async () => {
    // Skip if we just authenticated (prevents race condition)
    if (justAuthenticated.current) {
      justAuthenticated.current = false;
      setLoading(false);
      return;
    }

    try {
      // withCredentials is a global default; the browser attaches the
      // session_token cookie automatically.
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data.user);
    setToken(response.data.token);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (e) {
      // Logout endpoint failing shouldn't block client-side sign-out — we still
      // clear local session below. Log for diagnostics.
      console.warn('[auth] server logout failed, clearing client session anyway:', e?.message || e);
    }
    setUser(null);
    setToken(null);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
    // Mark that we just authenticated to prevent race condition with checkAuth
    justAuthenticated.current = true;
    setUser(response.data.user);
    if (response.data.token) {
      setToken(response.data.token);
    }
    setLoading(false);
    return response.data.user;
  };

  const acceptConsent = async () => {
    await axios.post(`${API}/auth/consent`, { accepted: true }, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    setUser(prev => ({ ...prev, consent_accepted: true }));
  };

  // Swap the current auth token in place (e.g. after credential rotation
  // where the backend issues a fresh JWT bound to the new token_version).
  // In-memory only — no persistence.
  const updateToken = (newToken) => {
    if (!newToken) return;
    setToken(newToken);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      token,
      login,
      register,
      logout,
      googleLogin,
      processGoogleSession,
      acceptConsent,
      checkAuth,
      updateToken,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requireConsent = true }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireConsent && !user.consent_accepted) {
    return <Navigate to="/consent" replace />;
  }

  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Auth Callback Handler
const AuthCallback = () => {
  const { processGoogleSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const params = new URLSearchParams(hash.replace('#', ''));
    const sessionId = params.get('session_id');

    if (sessionId) {
      processGoogleSession(sessionId)
        .then((user) => {
          if (user.consent_accepted) {
            navigate('/dashboard', { replace: true });
          } else {
            navigate('/consent', { replace: true });
          }
        })
        .catch(() => {
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/qr" element={<QRCodePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/case/:caseId" element={<CaseDetailPage />} />
      <Route path="/submit-sighting/:caseId" element={<SubmitSightingPage />} />
      
      {/* Protected Routes */}
      <Route path="/consent" element={
        <ProtectedRoute requireConsent={false}>
          <ConsentPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/privacy" element={
        <ProtectedRoute requireConsent={false}>
          <PrivacySettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/report" element={
        <ProtectedRoute>
          <ReportCasePage />
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={<SubscriptionPage />} />
      <Route path="/my-subscriptions" element={<MySubscriptionsPage />} />
      <Route path="/admin/moderation" element={<ModerationDashboardPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/set-password" element={
        <ProtectedRoute>
          <SetPasswordPage />
        </ProtectedRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminPage />
        </AdminRoute>
      } />
      <Route path="/admin/invoices" element={
        <AdminRoute>
          <AdminInvoicesPage />
        </AdminRoute>
      } />
      <Route path="/admin/backups" element={
        <AdminRoute>
          <AdminBackupsPage />
        </AdminRoute>
      } />
      
      {/* Moderator Route */}
      <Route path="/moderator" element={
        <ProtectedRoute>
          <ModeratorPage />
        </ProtectedRoute>
      } />
      
      {/* Legal Pages - Public */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/terms" element={<TermsConditionsPage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/anti-harassment" element={<AntiHarassmentPolicyPage />} />
      <Route path="/misuse-policy" element={<AntiHarassmentPolicyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/refund" element={<RefundPolicyPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/cancellation" element={<RefundPolicyPage />} />
      <Route path="/shipping" element={<ShippingDeliveryPolicyPage />} />
      <Route path="/shipping-policy" element={<ShippingDeliveryPolicyPage />} />
      <Route path="/delivery" element={<ShippingDeliveryPolicyPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Render SiteFooter globally on every public/logged-out page. We hide it
// only on the fullscreen app views (login/register/consent/reset) where the
// user hasn't landed on a public marketing page. Dashboard/Admin retain
// their own header + footer semantics via the internal chrome.
function GlobalFooter() {
  const location = useLocation();
  const HIDE_ON = [
    '/login', '/register', '/reset-password', '/forgot-password',
    '/consent', '/auth/callback',
  ];
  if (HIDE_ON.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return null;
  }
  return <SiteFooter />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <GlobalFooter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
